/// <amd-module name="Wiki/PageRenamePanel/Sources/PageRenamePanelSource" />
import { format } from "VSS/Core/Util/String";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";

import { WikiSearchRequest, WikiSearchResponse } from "Wiki/Clients/Search/Contracts/Search.Shared";
import { getSearchClient } from "Wiki/Clients/Search/RestClient/Search";
import { WorkItem, WorkItemExpand } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wiki/Clients/Wit/RestClient/WorkItemTracking";
import { JsonHttpResponse, JsonPatchDocument, JsonPatchOperation } from "Wiki/Clients/WitBatch/Contracts/WitBatchRestClient";
import {
    getWitBatchClient,
    getWorkItemPatchAddOperation,
    getWorkItemPatchTestOperation,
    getWorkItemRemoveLinkOperation
} from "Wiki/Clients/WitBatch/RestClient/WitBatchRestClient";
import { IArtifactResult, ILinkedArtifact } from "Wiki/PageRenamePanel/IPageRenamePanelSharedInterfaces";
import { getEncodedWikiPageUrlPath, replaceForwardSlashesToBackSlashes } from "Wiki/PageRenamePanel/PageRenamePanelHelper";
import { mapWorkItemIdToLinkedArtifact } from "Wiki/PageRenamePanel/WorkItemsArtifactHelper";
import { getLinkFromPath } from "Wiki/ViewCommon/Utils/PathHelper";

const searchTextFormat =
    "url:{0}" +
    " OR url:{0}.md" +
    " OR url:{0}#*" +
    " OR url:{0}/*" +
    " OR url:{1}" +
    " OR url:{1}#*" +
    " OR url:{1}\\*" +
    " OR url:*pagePath={2}" +
    " OR url:*pagePath={2}&*" +
    " OR url:*pagePath={2}#*" +
    " OR url:*pagePath={2}%2f*";

export class PageRenamePanelSource {
    constructor(private _pageContext: IVssPageContext) {}

    public queryWikiPageNameSearch(pagePath: string, skipResults: number, maxResultsCount: number, projectId: string): Promise<WikiSearchResponse> {
        const query: WikiSearchRequest = {
            searchText: _getSearchQueryText(pagePath),
            $skip: skipResults,
            $top: maxResultsCount,
            includeFacets: false
        } as WikiSearchRequest;

        return getSearchClient(this._pageContext).fetchWikiSearchResults(query, projectId);
    }

    public resolveArtifacts(workItemIds: number[]): Promise<IArtifactResult> {
        const artifacts: ILinkedArtifact[] = workItemIds.map((workitemId: number) => mapWorkItemIdToLinkedArtifact(workitemId));

        const legacyPlatformService = this._pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

        return legacyPlatformService
            .requireModules([
                "Presentation/Scripts/TFS/Controls/LinkedArtifacts/Logic/ArtifactResolver",
                "Presentation/Scripts/TFS/TFS.Host.TfsContext"
            ])
            .then(([artifactResolverModule, tfsContext]) => {
                const artifactResolver = artifactResolverModule.ArtifactResolver.getInstance();
                return artifactResolver.resolveArtifacts(
                    artifacts,
                    [
                        {
                            name: "Link",
                            refName: "Link"
                        }
                    ],
                    null,
                    tfsContext.TfsContext.getDefault()
                );
            });
    }

    public updateWorkItemsArtifactUris(workItemIdToUpdateUrls: { [key: number]: { [key: string]: string } }): Promise<{ [key: number]: boolean }> {
        const workItemIds: number[] = [];

        for (const workItemId in workItemIdToUpdateUrls) {
            if (workItemIdToUpdateUrls.hasOwnProperty(workItemId)) {
                workItemIds.push(parseInt(workItemId));
            }
        }

        return getWorkItemTrackingClient(this._pageContext)
            .getWorkItems(workItemIds, undefined, undefined, undefined, WorkItemExpand.Relations)
            .then((workItems: WorkItem[]) => {
                const updateJsonDocuments: [number, JsonPatchDocument][] = [];

                workItems.forEach((workItem: WorkItem) => {
                    const workItemId = workItem.id;

                    if (workItem && workItem.relations) {
                        const oldUrlToNewUrl = workItemIdToUpdateUrls[workItemId];
                        let updateOperations: JsonPatchOperation[] = [];

                        /**
                         * Find the index of the relation to delete.
                         * The order of deletion of relations should be in decreasing order of indices,
                         * otherwise deleting multiple relations in one work item will fail.
                         */
                        for (let i = workItem.relations.length - 1; i >= 0; --i) {
                            if (oldUrlToNewUrl[workItem.relations[i].url]) {
                                const currentUrl = workItem.relations[i].url;
                                updateOperations = updateOperations.concat(
                                    _getWorkItemJsonPatchOperationsForUpdatingRelation(i, currentUrl, oldUrlToNewUrl[currentUrl])
                                );
                            }
                        }

                        if (updateOperations.length) {
                            updateJsonDocuments.push([workItemId, updateOperations]);
                        }
                    }
                });

                return getWitBatchClient(this._pageContext)
                    .updateWorkItemsBatch(updateJsonDocuments)
                    .then((responses: JsonHttpResponse[]) => {
                        const workItemUpdateResults: { [key: number]: boolean } = {};

                        responses.forEach((response: JsonHttpResponse) => {
                            const workItemId = JSON.parse(response.body).id;
                            if (workItemId) {
                                workItemUpdateResults[workItemId] = response.code === 200; // SUCCESS
                            }
                        });

                        return workItemUpdateResults;
                    });
            });
    }
}

function _getWorkItemJsonPatchOperationsForUpdatingRelation(replaceIndex: number, expectedUrl: string, updatedUrl: string): JsonPatchOperation[] {
    const updateOperations: JsonPatchOperation[] = [];
    updateOperations.push(getWorkItemPatchTestOperation(replaceIndex, expectedUrl));
    updateOperations.push(getWorkItemRemoveLinkOperation(replaceIndex));
    updateOperations.push(getWorkItemPatchAddOperation(updatedUrl, "Wiki Page"));

    return updateOperations;
}

function _getSearchQueryText(pagePath: string): string {
    const linkPath = getLinkFromPath(pagePath);
    const urlPath = getEncodedWikiPageUrlPath(pagePath);
    const linkPathWithBackSlash = replaceForwardSlashesToBackSlashes(linkPath);

    // Replace '(' and ')' with '*' otherwise it will break the search query text into multiple terms
    return format(searchTextFormat, linkPath, linkPathWithBackSlash, urlPath).replace(/\(|\)|\\\(|\\\)/g, "*");
}
